/**
 * 计算字符串或 ArrayBuffer 的 SHA-256，返回十六进制小写字符串
 */
async function sha256Hex(data: string | ArrayBuffer): Promise<string> {
    const buffer = typeof data === "string"
        ? new TextEncoder().encode(data)
        : new Uint8Array(data)
    const hashBuffer = await crypto.subtle.digest("SHA-256", buffer)
    return Array.from(new Uint8Array(hashBuffer))
        .map(b => b.toString(16).padStart(2, "0"))
        .join("")
}

/**
 * 计算 HMAC-SHA256，返回十六进制小写字符串
 */
async function hmacSha256Hex(secret: string, message: string): Promise<string> {
    const keyMaterial = await crypto.subtle.importKey(
        "raw",
        new TextEncoder().encode(secret),   // secret 以 UTF-8 编码
        { name: "HMAC", hash: "SHA-256" },
        false,
        ["sign"]
    )
    const signatureBuffer = await crypto.subtle.sign(
        "HMAC",
        keyMaterial,
        new TextEncoder().encode(message)   // message 以 UTF-8 编码
    )
    return Array.from(new Uint8Array(signatureBuffer))
        .map(b => b.toString(16).padStart(2, "0"))
        .join("")
}

/**
 * 生成请求签名所需的 Headers
 * @param secret      当前有效 secret
 * @param method      HTTP 方法（GET/POST 等）
 * @param path        请求路径（不含域名和 query string）
 * @param body        请求 body 字符串，GET 请求传 ""
 * @param identityId  X-Identity-Id 的值，匿名时传 ""
 * @returns 需要附加到请求上的 Header 对象
 */
export async function buildSignatureHeaders(
    secret: string,
    method: string,
    path: string,
    body: string = "",
    identityId: string = ""
): Promise<{ "X-Timestamp": string; "X-Signature": string }> {
    const timestamp   = Math.floor(Date.now() / 1000).toString()   // 秒级时间戳
    const bodyHash    = await sha256Hex(body)
    const signingString = `${timestamp}\n${method.toUpperCase()}\n${path}\n${bodyHash}\n${identityId}`
    const signature   = await hmacSha256Hex(secret, signingString)

    return {
        "X-Timestamp": timestamp,
        "X-Signature": signature
    }
}